/**
 * Where `?next=` is allowed to send somebody after they sign in.
 *
 * ## The hole (found 17 Sep 2026, wiring WhatsApp deep links for TKT-1286)
 *
 * `/login` read `next` straight off the query and pushed it:
 *
 *   src/app/login/page.tsx               router.push(searchParams.get("next") || "/")
 *   src/components/auth/GoogleGsiButton  router.push(next || "/")
 *
 * Neither checked that it was a path, and `router.push` follows an absolute URL
 * off-site. So `https://app.mucka.ai/login?next=https://evil.example.com` signs
 * somebody in and then hands them to the attacker's page — in a Mucka browser
 * session, from a link whose domain really is ours. That is exactly the shape
 * phishing wants.
 *
 * 🚨 **The middleware's `loginUrl` docblock reads as though this was closed.**
 * It says "`next` is now only ever set here, from a server-derived pathname",
 * and it strips the query off its own redirect. That closed one ROUTE to the
 * hole and could not close the hole, because the attacker never needed the
 * middleware — they send the `/login` link themselves. A fix that removes one
 * way in reads, afterwards, like a fix that removed the way in.
 *
 * ## The rule was already written here, once
 *
 * `src/app/workspaces/switch-to/route.ts` had it right, comment and all:
 * *"Relative, single-slash paths only. Rejects `//evil.com` and `https://…`,
 * both of which browsers treat as absolute."* So this is not a new judgement.
 * It is that judgement in one place, applied wherever `next` is acted on, with
 * a test that reads the call sites — because the bug was never in a helper, it
 * was a raw `router.push` of a string somebody else chose.
 */

/**
 * `raw` if it is a same-origin path, else `fallback`.
 *
 * Deliberately an allowlist of shape rather than a blocklist of hosts: the
 * value has to begin with a single `/` and contain nothing a browser would
 * re-read as an authority or a scheme. Everything a real destination needs —
 * query, hash, nested path — passes untouched.
 */
export function safeNextPath(raw: string | null | undefined, fallback = '/'): string {
  if (typeof raw !== 'string' || raw.length === 0) return fallback;

  // Control characters, including tab and newline, are stripped or ignored by
  // browsers in a URL, so `/\tjavascript:…` and friends must not get through on
  // the strength of their first character.
  if (/[ -]/.test(raw)) return fallback;

  if (!raw.startsWith('/')) return fallback;

  // `//host` is protocol-relative and `/\host` is normalised to it — both are
  // an absolute destination wearing a path's first character.
  if (raw.startsWith('//') || raw.startsWith('/\\')) return fallback;

  return raw;
}
